import { ComponentType, ReactNode, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { signOut } from "aws-amplify/auth";
import { getConfigName } from "@helpers/funciones";
import { PATHS_COTIZADORES_V2 } from "@helpers/constantes";
import CotizadorFlux from "./CotizadorFlux";
import CotizadorMercader from "./CotizadorMercader";
import CotizadorProvidencia from "./CotizadorProvidencia";
import CotizadorWuWei from "./CotizadorWuWei";

export type CotizadorV2Type = "credito_simple";

/**
 * Cotizadores del diseño nuevo, por tipo de producto y empresa (`getConfigName`). Se agregan aquí conforme se
 * migran; mientras una empresa no esté en la lista sigue usando su cotizador de `Cotizadores`.
 */
const cotizadoresV2PorTipo: Record<CotizadorV2Type, Partial<Record<string, ComponentType>>> = {
  credito_simple: {
    flux: CotizadorFlux,
    mercader: CotizadorMercader,
    wuwei: CotizadorWuWei,
    providencia: CotizadorProvidencia,
  },
};

/** Ruta del cotizador legacy al que se regresa cuando la empresa todavía no tiene cotizador v2. */
const COTIZADORES_LEGACY_PATHS: Record<CotizadorV2Type, string> = {
  credito_simple: "/solicita_tu_credito",
};

const getCotizadorV2 = (type: string) =>
  cotizadoresV2PorTipo[type as CotizadorV2Type]?.[getConfigName(window.location.href)];

interface CotizadoresV2Props {
  type?: CotizadorV2Type;
}

/**
 * Despacha al cotizador v2 de la empresa. Al entrar se cierra la sesión anterior; si la empresa no tiene cotizador
 * v2, regresa al legacy conservando el query (p. ej. `?code=` del source_code).
 */
export const CotizadoresV2 = ({ type = "credito_simple" }: CotizadoresV2Props) => {
  const { search } = useLocation();
  const Component = getCotizadorV2(type);

  useEffect(() => {
    if (!Component) return;

    const logout = async () => {
      localStorage.clear();
      await signOut();
    };

    logout();
  }, [Component]);

  if (!Component) {
    return <Navigate to={`${COTIZADORES_LEGACY_PATHS[type]}${search}`} replace />;
  }

  return <Component />;
};

interface RedirectCotizadorV2Props {
  type: string;
  children: ReactNode;
}

/**
 * Para el cotizador legacy (`Cotizadores`): si la empresa ya tiene cotizador v2 manda a él conservando el query
 * (p. ej. `?code=` del source_code); si no, muestra `children`.
 */
export const RedirectCotizadorV2 = ({ type, children }: RedirectCotizadorV2Props) => {
  const { search } = useLocation();

  if (getCotizadorV2(type)) {
    return <Navigate to={`${PATHS_COTIZADORES_V2[type as CotizadorV2Type].path}${search}`} replace />;
  }

  return <>{children}</>;
};
